import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calculator, Info, AlertTriangle, CheckCircle2, Droplet, Syringe,
  FlaskConical, ChevronRight,
} from 'lucide-react';
import EditableText from '../components/EditableText';
import { useReconstitutionCalc } from './useReconstitutionCalc';

// ---------- small helpers ----------
const CHOICE_CLS = (active) =>
  `px-3 py-2 rounded-lg text-sm font-semibold border transition-colors ${
    active
      ? 'bg-red-500 text-white border-red-500'
      : 'bg-white text-slate-700 border-slate-200 hover:border-red-300 hover:text-red-700'
  }`;

const SYRINGES = [
  { label: '0.3ml', ml: 0.3 },
  { label: '0.5ml', ml: 0.5 },
  { label: '1.0ml', ml: 1.0 },
];

const VIAL_PRESETS = [10, 20, 30, 40, 60]; // mg
const WATER_PRESETS = [1, 2, 3, 5];       // ml
const DOSE_PRESETS = [0.1, 0.25, 0.5, 1]; // mg  (user requested mg)

const fmt = (n, d = 2) => (Number.isFinite(n) ? n.toFixed(d) : '—');

// ---------- choice row (preset + Custom) ----------
function ChoiceRow({ options, value, onChange, suffix, customStep = 0.01, customMin = 0 }) {
  const [forceCustom, setForceCustom] = React.useState(false);
  const inPresets = options.includes(value);
  const showCustom = forceCustom || !inPresets;

  const handlePreset = (o) => {
    setForceCustom(false);
    onChange(o);
  };
  const handleCustom = () => setForceCustom(true);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => handlePreset(o)}
          className={CHOICE_CLS(value === o && !showCustom)}>
          {o}{suffix}
        </button>
      ))}
      <button type="button" onClick={handleCustom} className={CHOICE_CLS(showCustom)}>
        Custom
      </button>
      {showCustom && (
        <div className="relative">
          <input
            type="number"
            min={customMin}
            step={customStep}
            value={value}
            autoFocus
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
            className="w-28 pl-3 pr-10 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/25 focus:border-red-400"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{suffix}</span>
        </div>
      )}
    </div>
  );
}

// ---------- main page ----------
const CalculatorPage = () => {
  const [syringeMl, setSyringeMl] = useState(1.0);
  const [vialMg, setVialMg] = useState(10);   // mg in the vial
  const [waterMl, setWaterMl] = useState(2);  // ml of bacteriostatic water
  const [doseMg, setDoseMg] = useState(0.5);  // desired dose in mg

  // ----- math (all in mg & ml) -----
  const calc = useReconstitutionCalc({ syringeMl, vialMg, waterMl, doseMg });

  const syringePct = Math.min(100, Math.max(0, (calc.drawUnits / calc.syringeUnits) * 100));

  return (
    <main className="max-w-5xl mx-auto px-6 py-12">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/" className="hover:text-red-700">Home</Link>
        <ChevronRight size={13} />
        <span>Reconstitution Calculator</span>
      </div>

      <div className="mt-5 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
          <Calculator size={22} className="text-red-600" />
        </div>
        <div>
          <EditableText
            k="calc.title" as="h1"
            className="text-[#0a0a0a] font-bold text-3xl md:text-4xl tracking-tight"
            fallback="Peptide Reconstitution Calculator"
          />
          <EditableText
            k="calc.subtitle" as="p" multiline
            className="text-slate-500 mt-2 max-w-2xl"
            fallback="Calculate the exact amount to draw in your syringe based on your peptide vial, solvent volume, and desired dosage."
          />
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Inputs */}
        <div className="lg:col-span-3 space-y-7 bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
          {/* Syringe */}
          <div>
            <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-slate-500 mb-3">
              <Syringe size={13} className="inline mr-1.5 -mt-0.5 text-slate-400" />
              Syringe Volume
            </label>
            <div className="flex gap-2 flex-wrap">
              {SYRINGES.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => setSyringeMl(s.ml)}
                  className={CHOICE_CLS(syringeMl === s.ml)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Vial qty */}
          <div>
            <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-slate-500 mb-3">
              <FlaskConical size={13} className="inline mr-1.5 -mt-0.5 text-slate-400" />
              Peptide Vial Quantity (mg)
            </label>
            <ChoiceRow
              options={VIAL_PRESETS}
              value={vialMg}
              onChange={setVialMg}
              suffix="mg"
              customStep={0.1}
            />
          </div>

          {/* Water */}
          <div>
            <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-slate-500 mb-3">
              <Droplet size={13} className="inline mr-1.5 -mt-0.5 text-slate-400" />
              Bacteriostatic Water (ml)
            </label>
            <ChoiceRow
              options={WATER_PRESETS}
              value={waterMl}
              onChange={setWaterMl}
              suffix="ml"
              customStep={0.1}
            />
          </div>

          {/* Desired dose in mg */}
          <div>
            <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-slate-500 mb-3">
              Desired Dosage (mg)
            </label>
            <ChoiceRow
              options={DOSE_PRESETS}
              value={doseMg}
              onChange={setDoseMg}
              suffix="mg"
              customStep={0.01}
            />
          </div>
        </div>

        {/* Result */}
        <div className="lg:col-span-2">
          <div className="sticky top-24 bg-white border border-slate-200 rounded-2xl p-6 sm:p-7">
            <div className="text-xs font-semibold tracking-[0.12em] uppercase text-slate-500 flex items-center gap-1.5">
              {calc.ok ? (
                <><CheckCircle2 size={14} className="text-red-600" /> Result</>
              ) : (
                <><AlertTriangle size={14} className="text-amber-500" /> {calc.overfills ? 'Too large for syringe' : 'Check inputs'}</>
              )}
            </div>

            {/* Syringe visual */}
            <div className="mt-5">
              <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1.5">
                <span>0</span>
                <span>{syringeMl * 100 * 0.25}</span>
                <span>{syringeMl * 100 * 0.5}</span>
                <span>{syringeMl * 100 * 0.75}</span>
                <span>{syringeMl * 100}</span>
              </div>
              <div className="relative h-3 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${calc.overfills ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${syringePct}%` }}
                />
              </div>
              <div className="text-[11px] text-slate-400 mt-1.5 text-center">
                Syringe capacity: {syringeMl * 100} units ({syringeMl}ml)
              </div>
            </div>

            {/* Big number */}
            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">Pull the syringe to:</p>
              <p className="text-5xl font-bold text-red-600 tracking-tight mt-2">
                {fmt(calc.drawUnits, 1)}
                <span className="text-xl text-slate-400 font-semibold ml-1">units</span>
              </p>
              <p className="text-sm text-slate-500 mt-1">({fmt(calc.drawMl, 3)} ml)</p>
            </div>

            {/* Details */}
            <div className="mt-6 pt-5 border-t border-slate-100 space-y-3 text-sm">
              <Row label="Concentration" value={`${fmt(calc.concentration_mg_per_ml, 2)} mg/ml`} sub={`(${fmt(calc.concentration_mcg_per_ml, 0)} mcg/ml)`} />
              <Row label="Dose volume" value={`${fmt(calc.drawMl, 3)} ml`} />
              <Row label="Doses per vial" value={Number.isFinite(calc.dosesPerVial) && calc.dosesPerVial > 0 ? fmt(calc.dosesPerVial, 1) : '—'} />
            </div>

            {calc.overfills && (
              <div className="mt-5 flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                <span>
                  The required draw ({fmt(calc.drawUnits, 1)} units) is larger than your selected syringe ({calc.syringeUnits} units).
                  Use a larger syringe, add more bacteriostatic water, or reduce your dose.
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Formula card */}
      <section className="mt-10 bg-slate-50/60 border border-slate-200 rounded-2xl p-6 sm:p-8">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-red-600 mt-1 flex-shrink-0" />
          <div className="text-sm text-slate-700">
            <p className="font-semibold text-[#0a0a0a] mb-2">How it's calculated</p>
            <ul className="space-y-1 list-disc pl-5 text-slate-600">
              <li><b>Concentration (mg/ml)</b> = Vial mg ÷ Water ml</li>
              <li><b>Volume to draw (ml)</b> = Desired dose (mg) ÷ Concentration (mg/ml)</li>
              <li><b>Units on syringe</b> = Volume to draw (ml) × 100 &nbsp;<span className="text-slate-400">(1 ml = 100 insulin units)</span></li>
              <li><b>Doses per vial</b> = Vial mg ÷ Desired dose (mg)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Educational content */}
      <section className="mt-14 prose prose-slate max-w-none">
        <EditableText k="calc.guide_title" as="h2"
          className="text-[#0a0a0a] font-bold text-2xl tracking-tight"
          fallback="Correct Peptide Reconstitution" />
        <EditableText k="calc.guide_intro" as="p" multiline
          className="text-slate-600 leading-relaxed mt-3"
          fallback="Peptides are supplied as lyophilized (freeze-dried) powder, which offers excellent stability for storage. Before use in research, peptides must be reconstituted (dissolved) in an appropriate solvent. Proper reconstitution technique is essential to maintain peptide integrity and ensure accurate dosing in your research." />

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {[
            {
              title: '1. Prepare Your Environment',
              body: 'Gather sterile gloves, safety goggles, alcohol swabs, a sterile syringe with needle, bacteriostatic water (BAC water), and your peptide vial. Work in a clean environment — absolute sterility and purity are essential for accurate research results.',
            },
            {
              title: '2. Preparation',
              body: 'If stored in the refrigerator, allow the peptide and bacteriostatic water to reach room temperature (15–20 minutes). Clean the rubber stopper of both vials with an alcohol swab and allow to air dry for a few seconds.',
            },
            {
              title: '3. Adding the Solvent',
              body: 'Draw your chosen amount of bacteriostatic water into the syringe. Tilt the peptide vial at ~45° and inject the water slowly so it runs down the interior wall — this minimises bubbles and protects the peptide. Never shake; gently swirl or roll the vial until fully dissolved.',
            },
            {
              title: '4. Storage',
              body: 'Short-term: refrigerate at 2–8°C for 3–4 weeks. Long-term: store at −20°C or lower for 3–4 months. Avoid repeated freeze-thaw cycles. For extended storage, aliquot into smaller portions before freezing.',
            },
          ].map((c) => (
            <div key={c.title} className="p-5 bg-white border border-slate-200 rounded-xl">
              <h3 className="font-semibold text-[#0a0a0a]">{c.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 p-5 border border-red-100 bg-red-50/40 rounded-xl text-sm text-slate-700 leading-relaxed">
          <b className="text-[#0a0a0a]">Tips:</b> Always use bacteriostatic water (not sterile water) — the preservative inhibits bacterial growth. Use a new sterile needle each withdrawal. Keep reconstituted peptides away from direct light. Label vials with reconstitution date and concentration. When in doubt about stability, prepare a fresh solution.
        </div>

        <p className="text-xs text-slate-400 mt-8">
          For in-vitro laboratory research use only. Not for human or veterinary use. Information provided for educational purposes.
        </p>
      </section>
    </main>
  );
};

const Row = ({ label, value, sub }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-slate-500">{label}</span>
    <span className="font-semibold text-[#0a0a0a] text-right">
      {value}
      {sub && <span className="block text-[11px] text-slate-400 font-normal">{sub}</span>}
    </span>
  </div>
);

export default CalculatorPage;
